import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import {
  getListingAgProductsAsync,
  getAgProductColumnsAsync,
} from '../actions/omsProductActions'

interface GroupedProductItem {
  id: number
  total_variants?: number
  sku?: string
  total_available_quantity?: number
  total_reserve_quantity?: number
  name?: string
  description?: string
  short_description?: string | null
  product_type?: string
  vendor?: string
  api_id?: string
  api_id2?: string | null
  api_code?: string
  api_status?: string
  api_created_at?: string
  api_updated_at?: string
  tags?: string
  isSyncReady?: boolean
  isActive?: boolean
  isDeleted?: boolean
  sync_status?: string
  sync_message?: string | null
  isCreatedByCommercium?: boolean
  type?: string
  created_at?: string
  updated_at?: string
  parent?: number | null
  platform?: number
  user?: number
  [key: string]: any
}

interface CatalogColumnItem {
  field: string
  headerName?: string
  label?: string
  name?: string
  width?: number
  sortable?: boolean
  resizable?: boolean
  filterable?: boolean
  allow_visiblity?: string
  advance_option?: {
    allow_sorting?: boolean
    allow_resize?: boolean
    allow_filter?: boolean
    filter_input_type?: string
    filter_options?: Array<{ label: string; value: any }>
  }
  [key: string]: any
}

interface PaginationState {
  pageNumber: number
  count: number
}

interface GroupedProductState {
  loading: boolean
  columnsLoading: boolean
  groupedProductList: GroupedProductItem[]
  productColumns: CatalogColumnItem[]
  pagination: PaginationState
  error: string | null
}

const initialState: GroupedProductState = {
  loading: false,
  columnsLoading: false,
  groupedProductList: [],
  productColumns: [],
  pagination: {
    pageNumber: 1,
    count: 0,
  },
  error: null,
}

const groupedProductSlice = createSlice({
  name: 'groupedProductSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(getListingAgProductsAsync.pending),
      (state) => {
        state.loading = true
        state.error = null
      }
    )

    builder.addMatcher(
      isAnyOf(getListingAgProductsAsync.rejected),
      (state, action: any) => {
        state.loading = false
        state.error =
          action?.error?.message || 'Failed to fetch grouped AG products'
      }
    )

    builder.addMatcher(
      isAnyOf(getListingAgProductsAsync.fulfilled),
      (state, action: any) => {
        state.loading = false

        const res = action.payload

        state.groupedProductList =
          res?.data?.results ??
          res?.results ??
          res?.data?.rows ??
          res?.rows ??
          []

        state.pagination.count =
          res?.data?.count ??
          res?.count ??
          res?.data?.total ??
          res?.total ??
          0

        state.pagination.pageNumber = action?.meta?.arg?.page ?? 1
      }
    )

    builder.addMatcher(
      isAnyOf(getAgProductColumnsAsync.pending),
      (state) => {
        state.columnsLoading = true
      }
    )

    builder.addMatcher(
      isAnyOf(getAgProductColumnsAsync.rejected),
      (state, action: any) => {
        state.columnsLoading = false
        state.error =
          action?.error?.message || 'Failed to fetch AG product columns'
      }
    )

    builder.addMatcher(
      isAnyOf(getAgProductColumnsAsync.fulfilled),
      (state, action: any) => {
        state.columnsLoading = false

        const res = action.payload

        state.productColumns =
          res?.data?.columns ??
          res?.columns ??
          res?.data?.results ??
          res?.results ??
          []
      }
    )
  },
})

export default groupedProductSlice.reducer